import { useEffect, useRef, useState, type FormEvent } from 'react';

import { GtoConnectLockup } from '../brand/GtoConnectLockup';
import { BrandFrame } from '../brand/BrandFrame';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import './LoginScreen.css';

type Stage = 'email' | 'code';

/** Пауза перед повторной отправкой кода, секунды. */
const RESEND_AFTER = 60;

export function LoginScreen({
  domain,
  onSignedIn,
  onBack,
}: {
  domain: string;
  onSignedIn: () => void;
  onBack: () => void;
}) {
  const [stage, setStage] = useState<Stage>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [cooldown, setCooldown] = useState(0);

  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (stage === 'email') emailRef.current?.focus();
    else codeRef.current?.focus();
  }, [stage]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  /**
   * Запрашивает код. `repeat` отличает повторную отправку: при первой
   * сообщать нечего — адрес и так написан на экране.
   */
  async function sendCode(target: string, repeat = false) {
    const result = await api.requestCode(target);
    setCooldown(RESEND_AFTER);

    if (result.delivery !== 'email') {
      setNotice('Почта не настроена — код выведен в консоль сервера');
    } else {
      setNotice(repeat ? 'Отправили новый код' : '');
    }
  }

  async function submitEmail(event: FormEvent) {
    event.preventDefault();
    const value = email.trim().toLowerCase();

    if (!value.endsWith(`@${domain}`)) {
      setError(`Вход только с рабочей почты @${domain}`);
      emailRef.current?.focus();
      return;
    }

    setError('');
    setBusy(true);
    try {
      await sendCode(value);
      setEmail(value);
      setStage('code');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось отправить код');
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api.verifyCode(email, code.trim());
      onSignedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось войти');
      setCode('');
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError('');
    setBusy(true);
    try {
      await sendCode(email, true);
      setCode('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось отправить код');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <div className="grid-bg" />
      <div className="aura login__aura" />

      <div className="login__box">
        <BrandFrame inset={-16} />

        <GtoConnectLockup markHeight={38} />

        {stage === 'email' ? (
          <form className="login__form" onSubmit={submitEmail} noValidate>
            <div>
              <h1 className="login__title">Вход для сотрудников</h1>
              <p className="login__lead">
                Введите рабочую почту — пришлём одноразовый код. Пароль придумывать не нужно.
              </p>
            </div>

            <label className="field">
              <span className="field__label">Рабочая почта</span>
              <input
                ref={emailRef}
                className="input"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                placeholder={`имя@${domain}`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={Boolean(error)}
              />
            </label>

            {error && <Message tone="error" text={error} />}

            <button className="btn btn--primary btn--lg btn--block" disabled={busy || !email.trim()}>
              {busy ? <span className="spinner" /> : <Icon name="send" size={18} />}
              {busy ? 'Отправляем' : 'Получить код'}
            </button>
          </form>
        ) : (
          <form className="login__form" onSubmit={submitCode} noValidate>
            <div>
              <h1 className="login__title">Код из письма</h1>
              {/* Адрес отдельной строкой: внутри предложения он ломался
                  посередине домена, и почта читалась как две разные. */}
              <p className="login__lead">Отправили шестизначный код на почту:</p>
              <p className="login__email">{email}</p>
              <p className="login__lead login__lead--tight">Он действует 10 минут.</p>
            </div>

            <label className="field">
              <span className="field__label">Код</span>
              <input
                ref={codeRef}
                className="input login__code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                aria-invalid={Boolean(error)}
              />
            </label>

            {error && <Message tone="error" text={error} />}
            {!error && notice && <Message tone="info" text={notice} />}

            <button
              className="btn btn--primary btn--lg btn--block"
              disabled={busy || code.length !== 6}
            >
              {busy ? <span className="spinner" /> : <Icon name="arrowRight" size={18} />}
              {busy ? 'Проверяем' : 'Войти'}
            </button>

            <div className="login__actions">
              <button
                type="button"
                className="login__link"
                onClick={() => {
                  setStage('email');
                  setError('');
                }}
              >
                Другая почта
              </button>
              <button
                type="button"
                className="login__link"
                onClick={resend}
                disabled={busy || cooldown > 0}
              >
                {cooldown > 0 ? `Отправить ещё раз через ${cooldown} с` : 'Отправить ещё раз'}
              </button>
            </div>
          </form>
        )}

        <button type="button" className="login__link login__back" onClick={onBack}>
          Я по ссылке-приглашению — вход не нужен
        </button>
      </div>
    </div>
  );
}

function Message({ tone, text }: { tone: 'error' | 'info'; text: string }) {
  return (
    <p className={`login__message login__message--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <Icon name={tone === 'error' ? 'alert' : 'check'} size={16} />
      {text}
    </p>
  );
}
